// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import {
  DARK_MODE,
  DEFAULT_DATE_FORMAT,
  type DarkModePreference,
  type DateFormat,
  WEIGHT_UNITS,
  type WeightUnit,
} from '@seedplanner/shared-utils';
import * as React from 'react';

const UserContext = React.createContext<UserContextType | undefined>(undefined);

interface UserContextType {
  readonly timezone: string;
  readonly permissions: {
    readonly can_edit_orders: boolean;
    readonly can_edit_crops_blends: boolean;
    readonly can_edit_tasks: boolean;
    readonly can_complete_tasks: boolean;
  };
  readonly preferredWeightUnit: WeightUnit;
  readonly preferredDateFormat: DateFormat;
  readonly darkModePreference: DarkModePreference;
  readonly setUserContext: (newUserContext: UserContextValue) => void;
  readonly setUserPreferredWeightUnit: (newWeightUnit: WeightUnit) => void;
  readonly setUserDarkModePreference: (newDarkModePreference: DarkModePreference) => void;
  readonly setUserPreferredDateFormat: (newDateFormat: DateFormat) => void;
  readonly resetUserContext: () => void;
}

const defaultUserContext: UserContextValue = {
  timezone: 'America/Chicago',
  permissions: {
    can_edit_orders: false,
    can_edit_crops_blends: false,
    can_edit_tasks: false,
    can_complete_tasks: false,
  },
  preferredWeightUnit: WEIGHT_UNITS.OUNCES,
  preferredDateFormat: DEFAULT_DATE_FORMAT,
  darkModePreference: DARK_MODE.SYSTEM,
};

export type UserContextValue = Omit<
  UserContextType,
  | 'setUserContext'
  | 'setUserPreferredWeightUnit'
  | 'setUserDarkModePreference'
  | 'resetUserContext'
  | 'setUserPreferredDateFormat'
>;

export type InitialUserState = UserContextValue | undefined;

export const useUserContext = (): UserContextType => {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};

export function UserContextProvider({
  initialState,
  children,
}: {
  readonly initialState: InitialUserState;
  readonly children: React.ReactNode;
}) {
  const [userContext, setUserContext] = React.useState(initialState ?? defaultUserContext);
  const setUserPreferredWeightUnit = (newWeightUnit: WeightUnit) => {
    setUserContext({ ...userContext, preferredWeightUnit: newWeightUnit });
  };
  const setUserDarkModePreference = (newDarkModePreference: DarkModePreference) => {
    setUserContext({ ...userContext, darkModePreference: newDarkModePreference });
  };
  const setUserPreferredDateFormat = (newDateFormat: DateFormat) => {
    setUserContext({ ...userContext, preferredDateFormat: newDateFormat });
  };
  const resetUserContext = () => {
    setUserContext(defaultUserContext);
  };
  return (
    <UserContext.Provider
      value={{
        ...userContext,
        setUserContext,
        setUserPreferredWeightUnit,
        setUserDarkModePreference,
        setUserPreferredDateFormat,
        resetUserContext,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
